import React, { useContext, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { getTicket } from "../../services/tickets.api";
import toastError from "../../errors/toastError";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  makeStyles,
  Typography,
} from "@material-ui/core";
import LockIcon from "@material-ui/icons/Lock";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import MessagesList from "../MessagesList";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

export default function TicketMessagesDialog({ open, handleClose, ticketId }) {
  const history = useHistory();
  const classes = useStyles();

  const { user } = useContext(AuthContext);

  const [, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    let delayDebounceFn = null;
    if (open) {
      setLoading(true);
      delayDebounceFn = setTimeout(() => {
        const fetchTicket = async () => {
          try {
            // La segunda copia de la misma regla hardcodeada (ver el porqué en
            // `components/Ticket/index.js`). Quien decide es el servidor:
            // `GET /tickets/:id` autoriza con `authorizeTicketRoom` y responde
            // 403, que se traduce abajo.
            const { data } = await getTicket(ticketId);

            setContact(data.contact);
            setTicket(data);
            setLoading(false);
          } catch (err) {
            setLoading(false);
            // El servidor denegó el acceso: mismo aviso de siempre, pero ahora
            // la decisión la tomó quien puede tomarla.
            if (err?.response?.status === 403) {
              toast.error(i18n.t("tickets.accessDenied"));
              history.push("/tickets");
              return;
            }
            toastError(err);
          }
        };
        fetchTicket();
      }, 500);
    }
    return () => {
      if (delayDebounceFn !== null) {
        clearTimeout(delayDebounceFn);
      }
    };
  }, [ticketId, user, history, open]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (!open || !ticket?.id) return;

    const socket = socketManager.getSocket(companyId);

    const subscribeToChat = () => socket.emit("joinChatBox", `${ticket.id}`);
    subscribeToChat();

    socket.on("ready", subscribeToChat);
    socket.on("connect", subscribeToChat);

    const handleTicketEvent = (data) => {
      if (data.action === "update" && data.ticket.id === ticket.id) {
        setTicket(data.ticket);
      }
      if (data.action === "delete" && data.ticketId === ticket.id) {
        history.push("/tickets");
      }
    };

    const handleContactEvent = (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    };

    socket.on(`company-${companyId}-ticket`, handleTicketEvent);
    socket.on(`company-${companyId}-contact`, handleContactEvent);

    return () => {
      socket.emit("leaveChatBox", `${ticket.id}`);
      socket.off("ready", subscribeToChat);
      socket.off("connect", subscribeToChat);
      socket.off(`company-${companyId}-ticket`, handleTicketEvent);
      socket.off(`company-${companyId}-contact`, handleContactEvent);
    };
  }, [ticketId, ticket?.id, history, open, socketManager]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const renderTicketInfo = () => {
    if (ticket.user !== undefined) {
      return (
        <TicketInfo
          contact={contact}
          ticket={ticket}
          onClick={handleDrawerOpen}
        />
      );
    }
  };

  const renderMessagesList = () => {
    return (
      <Box className={classes.root}>
        <MessagesList
          ticket={ticket}
          ticketId={ticket.id}
          isGroup={ticket.isGroup}
          readOnlyPreview
        ></MessagesList>
      </Box>
    );
  };

  return (
    <Dialog maxWidth="md" onClose={handleClose} open={open} fullWidth>
      <TicketHeader loading={loading}>{renderTicketInfo()}</TicketHeader>
      <ReplyMessageProvider>{renderMessagesList()}</ReplyMessageProvider>
      {/* ✅ NUEVO: Mensaje de modo lectura en la parte inferior, antes de los botones */}
      {ticket.status === "pending" && (
        <Box
          style={{
            padding: "12px 16px",
            backgroundColor: "#fff3cd",
            borderTop: "1px solid #ffc107",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            marginTop: "auto"
          }}
        >
          <LockIcon style={{ color: "#856404", fontSize: "18px" }} />
          <Typography
            variant="body2"
            style={{
              color: "#856404",
              fontWeight: "500",
              fontSize: "14px"
            }}
          >
            Modo lectura: Este ticket está pendiente. Debes aceptarlo para poder responder.
          </Typography>
        </Box>
      )}
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {i18n.t("ticketsManager.buttons.closeallTicket")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
